import { Avatar, AvatarGroup, Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectRole } from "src/redux/features/user/selectors/userState.selectors";
import { useAppSelector } from "src/redux/hooks/redux";
import { formatDate, IRoles } from "src/utils";
import { useSocket } from "../../context/SocketContext";
import { Composer } from "../Messages/components/Composer/Composer";
import { DropOverlay } from "../Messages/components/DropOverlay";
import { ImagePreviewModal } from "../Messages/components/ImagePreviewModal";
import { MessagesList } from "../Messages/components/MessageList";
import { useMessageComposer } from "../Messages/hooks/useMessageComposer";
import { buildChatDraftKey } from "../chat-dock/chatDrafts";
import { useMessageFileDrop } from "../Messages/hooks/useMessageFileDrop";
import { useMessagesScroll } from "../Messages/hooks/useMessageScroll";
import { RiveByKey } from "../riveAnimation";
import { avatarColorOptions } from "../ui/colors";
import { useJobChatMessagesQuery } from "./hooks/useJobChatMessagesQuery";
import { useJobChatMutation } from "./hooks/useJobChatMutation";
import { useJobChatRead } from "./hooks/useJobChatRead";
import { useJobChatSocket } from "./hooks/useJobChatSocket";
import { useJobChatSummaryQuery } from "./hooks/useJobChatSummaryQuery";
import {
  getJobChatCompanyDisplayName,
  isEmployeeRole,
  maskJobChatSenderForEmployee,
} from "./utils/jobChatEmployeeDisplay.utils";

const buildCurrentUserSender = (companyId: number, { uid, workforces, currentRole }: { uid: string; workforces: any[]; currentRole: string }) => {
  if (currentRole === IRoles.Employee) {
    const workforceUid = workforces?.find((w) => Number(w.companyId) === Number(companyId))?.uid;
    return { uid: workforceUid, type: "workforce" };
  }
  return { uid, type: "user" };
};

interface JobChatViewProps {
  jobEventUid: string;
  companyId?: number | string | null;
  status?: string;
  jobAlias?: string | null;
  hideHeader?: boolean;
  headerTitle?: string;
  isFocused?: boolean;
  focusMessageUid?: string | null;
  focusMessageText?: string | null;
  focusMessageRequestKey?: string | null;
  onFocusMessageConsumed?: () => void;
}

const getAvatarColor = (name: string) => {
  const value = name.split("").reduce((total, character) => total + character.charCodeAt(0), 0);
  return avatarColorOptions[value % avatarColorOptions.length];
};

export const JobChatView = ({
  jobEventUid,
  companyId: companyIdProp,
  status: statusProp,
  jobAlias: jobAliasProp,
  hideHeader = false,
  headerTitle,
  isFocused = true,
  focusMessageUid,
  focusMessageText,
  focusMessageRequestKey,
  onFocusMessageConsumed,
}: JobChatViewProps) => {
  const { uid, workforces, company } = useSelector((state: any) => state.userState);
  const { socket } = useSocket();
  const currentRole = useAppSelector(selectRole);

  const { data: chatSummary, isLoading: isLoadingSummary } = useJobChatSummaryQuery({ jobEventUid, companyId: companyIdProp });

  const companyId = companyIdProp ?? chatSummary?.companyId;
  const status = statusProp ?? chatSummary?.status;
  const jobAlias = jobAliasProp ?? chatSummary?.alias ?? chatSummary?.event?.alias ?? null;
  const participants = useMemo(() => {
    const members = [
      ...(chatSummary?.workforce || []),
      // Coworkers can access job chats implicitly, but are hidden from visible member avatars.
      // ...(chatSummary?.coworkers || []),
    ];
    return Array.from(new Map(members.map((member: any) => [member.uid, member])).values()) as any[];
  }, [chatSummary]);

  const [imagePreview, setImagePreview] = useState<{ open: boolean; url: string | null; name: string | null }>({
    open: false,
    url: null,
    name: null,
  });

  const [isActive, setIsActive] = useState<boolean>(status === "inProgress");

  useEffect(() => {
    setIsActive((status ?? chatSummary?.status) === "inProgress");
  }, [status, chatSummary?.status, jobEventUid]);

  const sender = useMemo(
    () => (companyId ? buildCurrentUserSender(companyId, { uid, workforces, currentRole }) : { uid, type: "user" }),
    [companyId, uid, workforces, currentRole],
  );

  const { messagesQuery } = useJobChatMessagesQuery({ jobEventUid, companyId });
  const hasMessages = useMemo(() => messagesQuery.data?.pages?.some((page: any) => (page?.items?.length ?? 0) > 0) ?? false, [messagesQuery.data]);
  const { messagesMutation } = useJobChatMutation(jobEventUid, companyId);
  const { markRead } = useJobChatRead(jobEventUid, companyId);
  const { messagesContainerRef, scrollToBottom, handleScroll } = useMessagesScroll(messagesQuery);

  const handleStatusChange = useCallback((nextStatus: string) => {
    setIsActive(nextStatus === "inProgress");
  }, []);

  useEffect(() => {
    if (jobEventUid && isFocused) markRead();
  }, [jobEventUid, markRead, isFocused]);

  const handleIncomingMessage = useCallback(() => {
    scrollToBottom();
    if (isFocused) markRead();
  }, [scrollToBottom, markRead, isFocused]);

  // Last time another participant read the chat (to show "Read" on my own messages live).
  // There's no initial per-participant snapshot from the backend, so this only reflects
  // reads that happen while this view is open.
  const [othersLastReadAt, setOthersLastReadAt] = useState<string | null>(null);

  const handleRead = useCallback(
    (reader: { type: string; uid?: string } | undefined, lastReadAt: string) => {
      const isMe = reader?.type === sender?.type && reader?.uid === sender?.uid;
      if (isMe) return;
      setOthersLastReadAt((prev) => (!prev || new Date(lastReadAt) > new Date(prev) ? lastReadAt : prev));
    },
    [sender?.type, sender?.uid],
  );

  const resolveIsRead = useCallback(
    (message: any) => (othersLastReadAt ? new Date(message.createdAt) <= new Date(othersLastReadAt) : false),
    [othersLastReadAt],
  );

  useJobChatSocket(socket, jobEventUid, companyId, handleIncomingMessage, handleStatusChange, handleRead);

  const messagesWithMemberRoles = useMemo(() => {
    if (!messagesQuery.data || !chatSummary) return messagesQuery;

    const companyDisplayName = getJobChatCompanyDisplayName(company);
    const members = [...(chatSummary.workforce || []), ...(chatSummary.coworkers || [])];
    return {
      ...messagesQuery,
      data: {
        ...messagesQuery.data,
        pages: messagesQuery.data.pages.map((page: any) => ({
          ...page,
          items: (page.items || []).map((message: any) => {
            const member = members.find((item: any) => item.uid === message.sender?.uid);
            const enriched = member
              ? {
                  ...message,
                  sender: {
                    ...message.sender,
                    avatar: message.sender?.avatar || member.avatar,
                    roleType:
                      member.roleName?.toLowerCase() === "business"
                        ? "Owner"
                        : message.sender?.roleType || member.roleName || member.role,
                  },
                }
              : message;

            return maskJobChatSenderForEmployee(enriched, sender, companyDisplayName, currentRole);
          }),
        })),
      },
    };
  }, [messagesQuery, chatSummary, sender, currentRole, company]);

  const handleMessageSent = useCallback(() => scrollToBottom(), [scrollToBottom]);

  const composer = useMessageComposer({
    messagesMutation,
    receiver: { jobEventUid },
    onMessageSent: handleMessageSent,
    draftKey: buildChatDraftKey({ companyId, scope: "job", id: jobEventUid }),
  });

  const { isDragging, handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useMessageFileDrop((files: File[]) => {
    composer.handleDragAndDropImages(files);
    composer.handleDragAndDropFiles(files);
  });

  const handleOpenImagePreview = (url: string, name: string) => setImagePreview({ open: true, url, name });
  const handleCloseImagePreview = () => setImagePreview({ open: false, url: null, name: null });

  if (isLoadingSummary || messagesQuery?.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (messagesQuery?.isError) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", color: "error.main", fontWeight: 600 }}>
        {(messagesQuery?.error as Error)?.message || "Error loading messages"}
      </Box>
    );
  }

  const companyTimeZone = company?.timeZone || "UTC";
  const chatDateRange =
    chatSummary?.startDate && chatSummary?.endDate
      ? `${formatDate(chatSummary.startDate, companyTimeZone, "M/d/yyyy, h:mm a")} – ${formatDate(
          chatSummary.endDate,
          companyTimeZone,
          "M/d/yyyy, h:mm a",
        )}`
      : "";

  return (
    <Box
      onDragEnter={isActive ? handleDragEnter : undefined}
      onDragLeave={isActive ? handleDragLeave : undefined}
      onDragOver={isActive ? handleDragOver : undefined}
      onDrop={isActive ? handleDrop : undefined}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        backgroundColor: "background.default",
      }}
    >
      {isActive && isDragging && <DropOverlay />}

      {chatSummary && !hideHeader ? (
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)",
            backgroundColor: "background.paper",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap variant="subtitle2" sx={{ fontWeight: headerTitle ? 600 : 700, fontSize: headerTitle ? "0.95rem" : undefined }}>
                {headerTitle || jobAlias || `Job #${chatSummary.jobId}`}
              </Typography>
              <Tooltip title={`Times are shown in the company time zone (${companyTimeZone}).`} arrow>
                <Typography variant="caption" color="text.secondary" component="span">
                  {headerTitle && jobAlias ? `${jobAlias} · ` : ""}
                  {chatDateRange}
                  {chatSummary.isActive ? " · Active" : " · Closed"}
                </Typography>
              </Tooltip>
            </Box>
            {participants.length > 0 && (
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.25, flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: "10px" }}>
                  Members
                </Typography>
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <Box>
                      {chatSummary.workforce?.length ? (
                        <Box>
                          <Typography sx={{ fontSize: 11, fontWeight: 700 }}>Workers</Typography>
                          {chatSummary.workforce.map((participant: any) => {
                            const name = [participant.firstName, participant.lastName].filter(Boolean).join(" ");
                            const role = participant.role?.toLowerCase() === "business" ? "Owner" : participant.role;
                            return (
                              <Typography key={participant.uid} sx={{ fontSize: 11 }}>
                                {role ? `${name} (${role})` : name}
                              </Typography>
                            );
                          })}
                        </Box>
                      ) : null}
                    </Box>
                  }
                >
                  <AvatarGroup
                    max={4}
                    sx={{
                      justifyContent: "flex-start",
                      width: "fit-content",
                      "& .MuiAvatar-root": { width: 24, height: 24, fontSize: "9px", border: "1px solid var(--fx-surface-base, #FFFFFF)" },
                    }}
                  >
                    {participants.map((participant) => {
                      const name = [participant.firstName, participant.lastName].filter(Boolean).join(" ");
                      return (
                        <Avatar
                          key={participant.uid}
                          src={participant.avatar || undefined}
                          alt={name}
                          sx={{ bgcolor: participant.avatar ? undefined : getAvatarColor(name) }}
                        >
                          {name[0]?.toUpperCase() || "?"}
                        </Avatar>
                      );
                    })}
                  </AvatarGroup>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      ) : null}

      {hasMessages ? (
        <MessagesList
          handleScroll={handleScroll}
          messagesContainerRef={messagesContainerRef}
          messagesQuery={messagesWithMemberRoles}
          sender={sender}
          onOpenImagePreview={handleOpenImagePreview}
          pendingMessages={composer.pendingMessages}
          onRetryMessage={composer.retryPendingMessage}
          resolveIsRead={resolveIsRead}
          focusMessageUid={focusMessageUid}
          focusMessageText={focusMessageText}
          focusMessageRequestKey={focusMessageRequestKey}
          onFocusMessageConsumed={onFocusMessageConsumed}
        />
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            px: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
            <RiveByKey riveKey="sectorworkflow" boxSize={170} />{" "}
          </Box>
          <Typography sx={{ mt: -3.5, color: "#f57c00", fontSize: "0.9rem", fontWeight: 800, textAlign: "center" }}>No messages yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.8rem", textAlign: "center" }}>
            There are no messages in this group chat.
          </Typography>
        </Box>
      )}

      {isActive ? (
        <Composer composer={composer} />
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            p: 2,
            borderTop: "1px solid var(--fx-surface-border, #E2E8F0)",
            backgroundColor: "background.paper",
            color: "text.secondary",
          }}
        >
          <LockOutlinedIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            This job chat is closed. Messages are read-only.
          </Typography>
        </Box>
      )}

      <ImagePreviewModal open={imagePreview.open} onClose={handleCloseImagePreview} imageUrl={imagePreview.url} imageName={imagePreview.name} />
    </Box>
  );
};
